import { ButtonGroup, Flex, IconButton, Pagination, Text } from '@chakra-ui/react';

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Pagination as PaginationType } from '@/types/pagination';
import { NavArrowLeft, NavArrowRight } from 'iconoir-react';

interface PaginationControlsProps {
  pagination: PaginationType;
  onPageChange: (page: number) => void;
  isLoading: boolean;
}

// Every pagination button is a 32px square carrying a Figtree label.
const PAGE_BUTTON_BASE = {
  minW: '32px',
  h: '32px',
  rounded: 'md',
  borderWidth: '1px',
  fontFamily: 'heading',
  fontSize: '12.5px',
  fontWeight: 'semibold',
} as const;

// Pages other than the current one, and the previous/next arrows.
const IDLE_PAGE_BUTTON_PROPS = {
  ...PAGE_BUTTON_BASE,
  variant: 'outline',
  bg: 'white',
  borderColor: 'hairline.control',
  color: 'ink',
  _hover: { bg: 'softCream', borderColor: 'hairline.controlHover' },
  _disabled: { opacity: 0.4 },
} as const;

const CURRENT_PAGE_BUTTON_PROPS = {
  ...PAGE_BUTTON_BASE,
  variant: 'solid',
  bg: 'primary.900',
  borderColor: 'primary.900',
  color: 'white',
  _hover: { bg: 'primary.900' },
} as const;

export const PaginationControls: React.FC<PaginationControlsProps> = ({
  pagination,
  onPageChange,
  isLoading,
}) => {
  const { t } = useTranslation();
  const { total, perPage, currentPage, lastPage, from, to } = pagination;

  // Handler for page changes from Chakra's component
  const handlePageChange = (details: { page: number }) => {
    if (!isLoading) {
      onPageChange(details.page);
    }
  };

  return (
    <Flex justifyContent="space-between" alignItems="center" mt={4} w="full">
      {/* Showing results text */}
      <Text fontSize="13px" color="inkMuted" whiteSpace="nowrap">
        {t('pagination.showing', { from, to, total })}
      </Text>

      {/* Chakra UI Pagination Component */}
      <Pagination.Root
        count={total}
        pageSize={perPage}
        page={currentPage}
        onPageChange={handlePageChange}
        siblingCount={1} // Show 1 page number on each side of the current page
      >
        {/* `xs` sizes the arrow icons (16px); the squares come from the props below. */}
        <ButtonGroup size="xs" colorPalette="gray">
          {/* Previous Page Button */}
          <Pagination.PrevTrigger asChild>
            <IconButton
              aria-label={t('pagination.previousPage')}
              disabled={currentPage === 1 || isLoading}
              {...IDLE_PAGE_BUTTON_PROPS}
            >
              <NavArrowLeft width={16} height={16} />
            </IconButton>
          </Pagination.PrevTrigger>

          {/* Page Number Items */}
          <Pagination.Items
            render={(page) => {
              const isCurrent = page.value === currentPage;

              return (
                <IconButton
                  aria-label={t('pagination.page', { page: page.value })}
                  aria-current={isCurrent ? 'page' : undefined}
                  {...(isCurrent ? CURRENT_PAGE_BUTTON_PROPS : IDLE_PAGE_BUTTON_PROPS)}
                >
                  {page.value}
                </IconButton>
              );
            }}
          />

          {/* Next Page Button */}
          <Pagination.NextTrigger asChild>
            <IconButton
              aria-label={t('pagination.nextPage')}
              disabled={currentPage === lastPage || isLoading}
              {...IDLE_PAGE_BUTTON_PROPS}
            >
              <NavArrowRight width={16} height={16} />
            </IconButton>
          </Pagination.NextTrigger>
        </ButtonGroup>
      </Pagination.Root>
    </Flex>
  );
};

export default PaginationControls;
